import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import TableContainer from "../../Components/Common/TableContainer";
import { Col, Row, Card, CardBody } from "reactstrap";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import { getCounsellingRequests, deleteRequest, updateStatus } from "utils/apiService";
import DeleteModal from "Components/Common/DeleteModal";
import { RejectModal } from "Components/Common/RejectModal";

function CounsellingReq() {
  document.title = "Counselling Requests";
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const data = await getCounsellingRequests();
      setRequests(data.response);
      /* console.log("Fetched Counselling Requests:", data.response); */
    } catch (err) {
      console.error("Failed to fetch counselling requests:", err);
    }
  };

  const handleEdit = (Request: any) => {
    /* setSelectedRequest(assignment);
    setEditModal(true); */
    console.log("Edit button pressed!", Request);
  };

  const handleDelete = async (request: any) => {
    setSelectedRequest(request);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedRequest) {
        await deleteRequest((selectedRequest as { id: number }).id);
        toast.success("Counselling request deleted successfully!");
        fetchRequests();
      }
    } catch (error) {
      toast.error("Failed to delete the request.");
    } finally {
      setDeleteModal(false);
      setSelectedRequest(null);
    }
  };

  const handleWhatsApp = (phoneNumber: string) => {
    if (phoneNumber) {
      const formattedNumber = phoneNumber.replace(/\D/g, "");
      window.open(`https://wa.me/${formattedNumber}`, "_blank");
    } else {
      alert("Phone number not available");
    }
  };

  const handleApprove = async (id: any) => {
    const result = await updateStatus({
      isApproved: true,
      id,
    });
    if (result.success) {
      toast.success("Status Updated Successfully!");
      fetchRequests();
    }
  };

  const handleReject = async (request: any) => {
    setSelectedRequest(request);
    setRejectionReason("");
    setRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      if (selectedRequest) {
        const result = await updateStatus({
          isApproved: false,
          id: (selectedRequest as { id: number }).id,
          reason: rejectionReason,
        });
        if (result.success) {
          toast.success("Request rejected successfully!");
          fetchRequests();
        } else {
          toast.error("Failed to reject the request.");
        }
      }
    } catch (error) {
      toast.error("Failed to reject the request.");
    } finally {
      setRejectModal(false);
      setSelectedRequest(null);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRejectionReason(e.target.value);
  };

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        cell: (cellProps) => {
          // Adding 1 because array index starts from 0
          return cellProps.row.index + 1;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Student Name",
        accessorKey: "student_name",
        enableColumnFilter: true,
      },
      {
        header: "Student Phone Number",
        accessorKey: "student_phone",
        enableColumnFilter: true,
        cell: ({ row }) => {
          const phone = row.original.student_phone || "";
          const countryCode = row.original.country_code || "";
          const formattedCountryCode = countryCode.startsWith("+")
            ? countryCode
            : `+${countryCode}`;

          return countryCode ? `${formattedCountryCode} ${phone}` : phone;
        },
      },
      {
        header: "Student Email",
        accessorKey: "student_email",
        enableColumnFilter: true,
      },
      {
        header: "Subject/Description",
        accessorKey: "subject",
        enableColumnFilter: true,
      },
      {
        header: "Country Code",
        accessorKey: "countryCode",
        enableColumnFilter: true,
        cell: ({ row }) => {
          const countryCode = row.original.countrycode;
          return countryCode ? `+${countryCode}` : "-";
        },
      },
      {
        header: "Scheduled Time",
        accessorKey: "schedule_time",
        enableColumnFilter: true,
        cell: ({ row }) => {
          const dateValue = row.original.schedule_time;
          return dateValue
            ? new Date(dateValue).toLocaleString(undefined, {
              timeZone: "UTC",
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
            : "";
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: true,
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const request = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              {(!request?.status || request.status === "Pending") && (
                <>
                  <Link
                    to="#"
                    className="text-success mx-2"
                    onClick={() => handleApprove(request.id)}
                  >
                    <i className="mdi mdi-check-circle font-size-18" />
                  </Link>

                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => handleReject(request)}
                  >
                    <i className="mdi mdi-close-circle font-size-18" />
                  </Link>
                </>
              )}

              <Link
                to="#"
                className="text-success"
                onClick={() => handleWhatsApp(request.student_phone)}
              >
                <i className="mdi mdi-whatsapp font-size-18" />
              </Link>
              <Link
                to="#"
                className="text-danger"
                onClick={() => handleDelete(request)}
              >
                <i className="mdi mdi-delete font-size-18" />
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteModal(false)}
        message="Are you sure you want to delete this counselling request ? You won't be able to revert this!"
      />
      <RejectModal
        show={rejectModal}
        onRejectClick={confirmReject}
        onCloseClick={() => setRejectModal(false)}
        onChange={handleReasonChange}
        reason={rejectionReason}
        title="Reject Counselling Request"
        message="Are you sure you want to reject this counselling request? This action cannot be undone."
        rejectButtonText="Reject Request"
        reasonPlaceholder="Please provide a reason for rejecting this request"
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs breadcrumbItem="Counselling requests" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={requests}
                    isGlobalFilter={false}
                    isAddButton={false}
                    customPageSize={10}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

export default CounsellingReq;
